import type { Recipe } from './compose';

/**
 * One picture per move, by id: a scene name from scenes*.ts (or a "subject+effect" composition),
 * optional variant `v`, mirror `!` and a `tone` colour. `check-unique` (docs/review/c0-icons)
 * fails when two moves would look the same.
 */
type Spec = string | [string, Partial<Recipe>];
const S = (spec: Spec): Recipe => (typeof spec === 'string' ? { scene: spec } : { scene: spec[0], ...spec[1] });

const BLOOD = '#e0404a';
const POISON = '#9ce04a';
const FIRE = '#ff8a2a';
const ICE = '#bfeaff';

const T: Record<string, Spec> = {
  // ศิลาเก้ากระบี่ (nine swords)
  'ns-thunder': 'bolt',
  'ns-pierce': 'pierce',
  'ns-nine': 'nine',
  'ns-calm': 'swordRest',
  'ns-sweep': 'sword+ring',
  'ns-rush': 'lunge',
  'ns-oath': 'aura',
  'ns-heaven': 'boltBig',
  // ฝ่ามือมังกรทองเก้าสวรรค์ (C0-VFX, an original art): a golden dragon in each move's pose.
  'dp9-burst': ['goldDragon', { v: 0 }],
  'dp9-lance': ['goldDragon', { v: 1 }],
  'dp9-twin': ['goldDragon', { v: 2 }],
  'dp9-rend': ['goldDragon', { v: 3 }],
  'dp9-stomp': ['goldDragon', { v: 4 }],
  'dp9-whirl': ['goldDragon', { v: 5 }],
  'dp9-double': ['goldDragon', { v: 6 }],
  'dp9-sky': ['goldDragon', { v: 7 }],
  // หุบเขาเมฆา cloud palm
  'cp-wave': 'palm',
  'cp-seize': 'grab',
  'cp-storm': 'whirl',
  'cp-breath': 'breath',
  'cp-drift': 'cloudDrift',
  'cp-roar': 'palm2',
  'cp-ring': 'palm+ring',
  'cp-heaven': 'palmCloud',
  // breeze sword
  'bs-gust': 'wind',
  'bs-cloud': 'sword+smoke',
  'bs-sweep': 'whirlLeaf',
  'bs-bamboo': 'bamboo',
  'bs-glide': 'sword+speed',
  'bs-mist': 'mist',
  'bs-reed': 'ribbon',
  'bs-tempest': ['whirl', { v: 3 }],
  // taiji fist
  'tf-push': 'palm+waves',
  'tf-grasp': ['grab', { flip: true }],
  'tf-whip': 'palm+trail',
  'tf-clouds': 'taijiHands',
  'tf-yield': 'taijiWheel',
  'tf-step': 'foot+ring',
  'tf-wheel': 'taiji',
  'tf-supreme': 'taijiBig',
  // taiji sword
  'ts-circle': 'yinSword',
  'ts-yinyang': 'yinBreak',
  'ts-stars': 'stars',
  'ts-still': 'sword+dew',
  'ts-flow': 'combo',
  'ts-thread': 'sword+drops',
  'ts-focus': 'yinFocus',
  'ts-heaven': 'taijiBeam',
  // snow sword
  'ss-shift': 'snow',
  'ss-frost': 'snowdash',
  'ss-gale': 'blizzard',
  'ss-walk': 'snowstep',
  'ss-ice': 'icebreak',
  'ss-pride': 'snowpride',
  'ss-trace': 'sword+flake',
  'ss-hatred': 'snowblink',
  // arhat fist
  'ar-strike': 'vajra',
  'ar-palm': ['vajra', { v: 1 }],
  'ar-bell': 'bell',
  'ar-sweep': 'foot+cracks',
  'ar-break': 'quake',
  'ar-lion': 'shout',
  'ar-step': ['vajra', { v: 2 }],
  'ar-buddha': 'fist+halo',
  // brocade sabre
  'jy-cut': 'sabre',
  'jy-cross': 'crossX',
  'jy-whirl': 'sabre+ring',
  'jy-lunge': 'lungeSabre',
  'jy-shatter': 'sabreBreak',
  'jy-oath': 'lantern',
  'jy-execute': 'chop',
  'jy-dragon': 'dragonWave',
  // bliss palm
  'jl-claw': 'claw+stars',
  'jl-needle': ['needles', { v: 0 }],
  'jl-charm': 'charmEye',
  'jl-step': 'ghostStep',
  'jl-soul': 'soul',
  'jl-drain': 'drainHand',
  'jl-bloom': 'bloom',
  'jl-abyss': 'palmDark',
  // brush sword
  'jz-stroke': 'brush',
  'jz-poem': 'poem',
  'jz-ink': 'ink',
  'jz-seal': 'seal',
  'jz-ward': 'enso',
  'jz-focus': 'sword+eye',
  'jz-break': 'brushBreak',
  'jz-scroll': 'scrollOpen',
  // blood sabre
  'bs-slash': 'blood',
  'bs-rend': ['slash', { tone: BLOOD }],
  'bs-whirl': 'sabre+drops',
  'bs-charge': ['sabre+speed', { tone: BLOOD }],
  'bs-break': ['sabre+cracks', { tone: BLOOD }],
  'bs-frenzy': 'flame',
  'bs-drink': 'bloodDrink',
  'bs-massacre': 'bloodstorm',
  // prajna palm
  'pp-wave': 'palm+burst',
  'pp-shatter': 'fist+cracks',
  'pp-thousand': 'palms3',
  'pp-calm': 'palm+halo',
  'pp-dharma': 'palm+cracks',
  'pp-lotus': 'calm',
  'pp-vow': 'bellShield',
  'pp-tathagata': 'palmHalo',
  // demon sword
  'fm-subdue': 'sabre+smoke',
  'fm-heavy': 'slamSword',
  'fm-cross': ['combo', { flip: true }],
  'fm-wheel': 'sabre+trail',
  'fm-stride': 'sabre+waves',
  'fm-bell': ['bell', { flip: true }],
  'fm-zen': 'zen',
  'fm-vanquish': 'sabre+flame',
  // arrest hand
  'ah-grip': 'claw+ring',
  'ah-throw': 'claw+trail',
  'ah-chain': 'chain',
  'ah-seal': 'palm+seal',
  'ah-sweep': 'foot+trail',
  'ah-interrogate': 'palm+stars',
  'ah-decree': 'banner',
  'ah-imperial': 'sealGold',
  // spring sabre
  'sp-flash': 'sabre+stars',
  'sp-pierce': 'dagger+speed',
  'sp-petals': 'petals',
  'sp-bloom': 'whirlPetals',
  'sp-hunt': 'sabre+eye',
  'sp-verdict': ['chop', { flip: true }],
  'sp-loyal': 'sabre+halo',
  'sp-decree': ['boltBig', { flip: true }],
  // moon sword
  'ms-moon': 'moonSword',
  'ms-veil': 'veil',
  'ms-longing': 'sword+petals',
  'ms-step': ['ghostStep', { flip: true }],
  'ms-night': 'shadow',
  'ms-kiss': 'dagger+petals',
  'ms-mirror': 'mirror',
  'ms-eclipse': 'eclipse',
  // demon dance
  'dd-sleeve': 'sleeve',
  'dd-twirl': ['whirl', { v: 5 }],
  'dd-petals': ['petals', { flip: true }],
  'dd-drain': ['drainHand', { flip: true }],
  'dd-mist': ['wind', { flip: true }],
  'dd-trance': 'palm+ghost',
  'dd-dew': 'dew',
  'dd-heaven': 'palm+eye',
  // six pulse
  'sf-finger': 'finger',
  'sf-pulse': ['finger', { flip: true }],
  'sf-six': 'fan6',
  'sf-hold': 'fist+seal',
  'sf-qin': 'qin',
  'sf-drift': ['mist', { flip: true }],
  'sf-water': 'palm+dew',
  'sf-divine': 'finger+halo',
  // sage sword
  'sg-line': ['brush', { flip: true }],
  'sg-verse': ['poem', { flip: true }],
  'sg-rhyme': 'staff+waves',
  'sg-stroke': 'staff+trail',
  'sg-scroll': 'staff+stars',
  'sg-insight': ['sword+eye', { flip: true }],
  'sg-screen': 'screen',
  'sg-masterpiece': ['scrollOpen', { flip: true }],
  // blood claw
  'bc-rake': ['claw+drops', { tone: BLOOD }],
  'bc-crush': ['fist+cracks', { tone: BLOOD, flip: true }],
  'bc-maul': ['fist+drops', { tone: BLOOD }],
  'bc-pounce': ['claw+speed', { tone: BLOOD }],
  'bc-roar': ['claw+halo', { tone: BLOOD }],
  'bc-feast': 'staff+drops',
  'bc-fury': 'fist+flame',
  'bc-tiger': 'tigerFace',
  // red cliff sabre
  'rc-edge': 'sabre+bolt',
  'rc-split': 'sword+cracks',
  'rc-tide': 'bloodTide',
  'rc-hunt': ['sabre+speed', { flip: true }],
  'rc-reap': 'reap',
  'rc-cliff': 'boulder',
  'rc-drink': ['bloodDrink', { flip: true }],
  'rc-sea': ['bloodstorm', { flip: true }],
  // dragon palm
  'dp-regret': 'palmDragon',
  'dp-tail': 'claw+smoke',
  'dp-field': 'fist+waves',
  'dp-leap': ['palm', { flip: true }],
  'dp-soar': 'palm+bolt',
  'dp-scales': 'scales',
  'dp-hidden': ['breath', { flip: true }],
  'dp-eighteen': 'fistWheel',
  // hidden arts
  'hw-dart': ['needles', { v: 1 }],
  'hw-snare': 'snare',
  'hw-knives': 'knives',
  'hw-rain': 'rain',
  'hw-caltrops': 'caltrops',
  'hw-smoke': 'smoke',
  'hw-aim': 'crosshair',
  'hw-stars': 'shuriken',
  // monsters and bosses
  'wolf-bite': 'bite',
  'bandit-slash': 'sabre+burst',
  'bandit-crush': 'club',
  'assassin-stab': 'dagger+smoke',
  'assassin-feint': 'dagger+ghost',
  'monk-staff': 'staff+burst',
  'monk-sweep': 'staff+cracks',
  'bear-swipe': 'paw',
  'bear-maul': 'claw+cracks',
  'knife-throw': 'dagger+stars',
  'jiangshi-claw': ['claw+ghost', { tone: POISON }],
  'boar-gore': 'tusk',
  'boar-charge': 'foot+speed',
  'viper-fang': 'venomFang',
  'scorpion-pincer': 'pincer',
  'scorpion-sting': 'stinger',
  'leopard-claw': 'claw+burst',
  'bat-fang': 'batFang',
  'spider-fang': 'spiderFang',
  'spider-web': 'web',
  'centipede-bite': 'centipede',
  'python-strike': 'snakeHead',
  'python-coil': 'coil',
  'fox-claw': 'claw+petals',
  'fox-charm': 'foxTail',
  'tiger-claw': 'claw+flame',
  'tiger-pounce': ['tigerFace', { flip: true, tone: '#f0a030' }],
  'boss-slash': 'sword+flame',
  'boss-cleave': 'cleave',
  'wb-rain': 'swordRain',
  'boss-ring': 'ring',
  'hidden-darts': ['needles', { v: 2 }],
  'hidden-needles': ['needles', { flip: true }],
  'dg-wave-cut': 'sabre+leaf',
  'dg-kick': 'foot+burst',
  'dg-harpoon-throw': 'dgHarpoon',
  'dg-harpoon-hook': 'harpoonHook',
  'dg-backstep': 'foot+smoke',
  'dg-powder-dart': 'dagger+dew',
  'dg-sleep-powder': 'dgSleep',
  'dg-salt-salve': 'saltJar',
  'dg-chain-lash': 'dgChain',
  'dg-dragon-chain': 'dragonChain',
  'dg-chain-spin': ['whirl', { v: 7 }],
  'dg-club-sweep': 'staff+leaf',
  'dg-twin-club': 'staff+ring',
  'dg-dagger-flurry': 'dagger+trail',
  'dg-dagger-break': 'dagger+cracks',
  'dg-triple-wave': 'wave',
  'dg-frost-cut': 'sabre+flake',
  'dg-moon-decoys': 'sabre+ghost',
  'dg-surf-lane': 'foot+waves',
  'dg-shark-slash': 'fin',
  'dg-shark-crush': 'sharkJaw',
  'dg-giant-palm': 'palm+drops',
  'dg-sea-turn': ['wave', { flip: true }],
  'dg-whirlpool': 'dgWhirlpool',
  'dg-sea-split': 'sword+waves',
  'dg-iron-body': 'dgIron',
  'wb-ape-sweep': 'fist+trail',
  'wb-ape-slam': 'fist+burst',
  'wb-ape-boulder': 'fist+bolt',
  'wb-ape-grab': 'palm+smoke',
  'wb-ape-ledge': 'fist+smoke',
  'wb-ape-mimic': 'mimic',
  'wb-ape-tree': 'treeSwing',
  'wb-ape-leap': 'leap',
  'wb-ape-chest': 'apeChest',
  'wb-bird-peck': 'beak',
  'wb-bird-fan': 'birdFan',
  'wb-bird-kick': 'claw+bolt',
  'wb-bird-rain': 'feather',
  'wb-bird-dive': ['feather', { flip: true }],
  'wb-bird-sky-ring': 'ringFeather',
  'wb-bird-sky-burn': 'skyBurn',
  'wb-bird-eggs': 'eggs',
  'wb-bird-swoop': 'sunBird',
  'wb-turtle-bite': ['bite', { flip: true, tone: '#4fa070' }],
  'wb-turtle-flipper': 'turtleFlipper',
  'wb-turtle-rings': ['ring', { flip: true }],
  'wb-turtle-bolts': 'turtleBolts',
  'wb-turtle-tide': 'turtleTide',
  'wb-turtle-ram': 'turtleRam',
  'wb-turtle-thousand': 'turtleThousand',
};

export const RECIPES: Record<string, Recipe> = Object.fromEntries(Object.entries(T).map(([k, v]) => [k, S(v)]));
void ICE;
void FIRE;
